/**
 * Pure H OS · Phase D Wave 4 · Warehouses.
 * Physical inventory locations — raw / finished / ecommerce / retail.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { WAREHOUSES, WarehouseKind } from "../mocks/warehousesData";
import { STOCK } from "../mocks/commerceData";

const KIND_LABEL: Record<WarehouseKind, string> = {
  raw: "Raw", finished: "Finished", ecommerce: "E-commerce", retail: "Retail",
};
function utilizationSeverity(pct: number): "low" | "medium" | "high" {
  if (pct >= 85) return "high";
  if (pct >= 70) return "medium";
  return "low";
}

type Tab = "all" | WarehouseKind;

export default function Warehouses() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = useMemo(() => (tab === "all" ? WAREHOUSES : WAREHOUSES.filter((w) => w.kind === tab)), [tab]);
  const current = openId ? WAREHOUSES.find((w) => w.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="warehouses-page">
      <Card testId="wh-filters">
        <TabBar<Tab>
          testId="wh-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",       label: `All ${WAREHOUSES.length}` },
            { key: "raw",       label: "Raw" },
            { key: "finished",  label: "Finished" },
            { key: "ecommerce", label: "E-commerce" },
            { key: "retail",    label: "Retail" },
          ]}
        />
      </Card>

      <Card padded={false} testId="wh-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Warehouse Network" title={`${filtered.length} location${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>Every physical inventory location across ORVAN.</div>
        </div>
        <div className="p-2">
          {filtered.map((w) => (
            <ListRow key={w.id} testId={`wh-${w.id}`}
              onClick={() => setOpenId(w.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{w.code}</span>
                  <span>{w.name}</span>
                </span>
              }
              sublabel={<>{KIND_LABEL[w.kind]} · {w.location} · {w.skuCount} SKUs · EGP {w.valueEGP.toLocaleString()}</>}
              trailing={<SeverityBadge severity={utilizationSeverity(w.utilizationPct)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="wh-drawer" title={current ? current.name : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${KIND_LABEL[current.kind]} · ${current.code}`} title={current.location} />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>SKUs</div>
                <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.skuCount}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Utilization</div>
                <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.utilizationPct}%</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Value</div>
                <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.valueEGP.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Manager</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.manager}</div>
              </div>
            </div>

            {/* Warehouse SKU drill-down — for finished/ecommerce/retail we surface STOCK rows.
                Ordering by daysCover ascending — lowest cover surfaces first (risk). */}
            {(() => {
              const skuRows = current.kind === "raw"
                ? []
                : STOCK.slice().sort((a, b) => a.daysCover - b.daysCover).slice(0, Math.min(STOCK.length, current.skuCount || 12));
              return (
                <div data-testid={`wh-skus-${current.id}`}>
                  <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>SKUs held here · {skuRows.length}</div>
                  {skuRows.length === 0 ? (
                    <div className="mt-2 text-xs" style={{ color: "var(--ph-muted)" }}>Raw materials warehouse — SKU breakdown not applicable.</div>
                  ) : (
                    <div className="mt-3 rounded overflow-hidden" style={{ border: "1px solid var(--ph-border-subtle)" }}>
                      <div className="grid grid-cols-6 text-[10px] uppercase tracking-[0.14em] px-3 py-2" style={{ background: "var(--ph-surface-subtle)", color: "var(--ph-muted)" }}>
                        <div className="col-span-2">SKU · Name</div>
                        <div className="text-right">On hand</div>
                        <div className="text-right">Incoming</div>
                        <div className="text-right">Reserved</div>
                        <div className="text-right">Days cover</div>
                      </div>
                      {skuRows.map((r) => (
                        <div key={r.sku} data-testid={`wh-sku-${r.sku}`} className="grid grid-cols-6 text-xs px-3 py-2 tabular-nums" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                          <div className="col-span-2">
                            <div style={{ color: "var(--ph-text)" }}>{r.sku}</div>
                            <div className="text-[10px]" style={{ color: "var(--ph-muted)" }}>{r.name}</div>
                          </div>
                          <div className="text-right" style={{ color: "var(--ph-text)" }}>{r.onHand}</div>
                          <div className="text-right" style={{ color: "var(--ph-text)" }}>{r.incoming}</div>
                          <div className="text-right" style={{ color: "var(--ph-text)" }}>{r.reserved}</div>
                          <div className="text-right" style={{ color: r.level === "critical" || r.level === "low" ? "var(--ph-danger)" : "var(--ph-text)" }}>{r.daysCover}d</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </Drawer>
    </div>
  );
}
